import { useQuery } from "@apollo/client";
import {
  Box,
  Button,
  Card,
  Center,
  createStyles,
  Group,
  LoadingOverlay,
  Menu,
  ScrollArea,
  Select,
  SimpleGrid,
  Text,
  UnstyledButton,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { showNotification } from "@mantine/notifications";
import { IconChevronDown, IconChevronUp, IconSelector } from "@tabler/icons";
import { REPORT } from "apollo/queries";
import B2bTable from "components/reusable/b2bTable";
import { customLoader } from "components/utilities/loader";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useEffect, useState } from "react";
import { formatLocalDate, formatNumber } from "utiles/url";
import * as XLSX from "xlsx";
import BusinesFilter from "./bussiness";
import WarehouseFilter from "./warehouse";

const headers = ["Order code", "Price", "Items", "Busines", "Date"];

const useStyles = createStyles((theme) => ({
  th: {
    padding: "0 !important",
  },
  control: {
    width: "100%",
    padding: `${theme.spacing.xs}px ${theme.spacing.md}px`,
    "&:hover": {
      backgroundColor:
        theme.colorScheme === "dark"
          ? theme.colors.dark[6]
          : theme.colors.gray[0],
    },
  },
  icon: {
    width: 15,
    height: 21,
    borderRadius: 21,
  },
  thh: {
    color: "#666666",
    fontFamily: "'__Inter_aaf875','__Inter_Fallback_aaf875'",
    fontSize: "10px",
    textTransform: "uppercase",
    fontWeight: "bold",
  },
}));

function Th({ children, sortable, sorted, reversed, onSort }) {
  const { classes } = useStyles();
  const Icon = sorted
    ? reversed
      ? IconChevronUp
      : IconChevronDown
    : IconSelector;

  return (
    <th className={classes.th}>
      <UnstyledButton
        onClick={sortable ? onSort : null}
        className={classes.control}
      >
        <Group position="apart" spacing={5}>
          <Text weight={500} size="sm">
            {children}
          </Text>
          {sortable && (
            <Center className={classes.icon}>
              <Icon size={14} stroke={1.5} />
            </Center>
          )}
        </Group>
      </UnstyledButton>
    </th>
  );
}

const SalesReport = () => {
  const [size, setSize] = useState("50");
  const [activePage, setActivePage] = useState(1);
  const [total, setTotal] = useState(0);
  const [business, setBusiness] = useState(null);
  const [warehouse, setSetWarehouse] = useState(null);
  const [timeRange, setTimeRange] = useState(null);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const { loading, data, error, refetch } = useQuery(REPORT, {
    variables: {
      first: parseInt(size),
      page: activePage,
      period: timeRange || undefined,
      startDate: selectedStartDate
        ? formatLocalDate(selectedStartDate)
        : undefined,
      endDate: selectedEndDate ? formatLocalDate(selectedEndDate) : undefined,
      warehouse: warehouse || undefined,
      supplier_business: business || undefined,
      orderBy: undefined,
    },
    skip: !size || !activePage,
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message: error?.graphQLErrors?.[0]?.message || "Failed to get reports ",
      });
    },
  });

  const headerData = [
    {
      label: "Order code",
      key: "orderCode",
      sortable: false,
      searchable: false,
      render: (rowData) => (
        <span style={{ width: "20px" }}>{rowData.orderCode}</span>
      ),
    },
    {
      label: "Price",
      key: "price",
      sortable: false,
      searchable: false,
      render: (rowData) => <span>{formatNumber(rowData.price)}</span>,
    },
    {
      label: "Items",
      key: "numberOfItems",
      sortable: false,
      searchable: false,
      render: (rowData) => <span>{rowData.numberOfItems}</span>,
    },
    {
      label: "Busines Name",
      key: "supplier_business",
      sortable: false,
      searchable: false,
      render: (rowData) => <span>{rowData.supplier_business}</span>,
    },
    {
      label: "Date",
      key: "date",
      sortable: false,
      searchable: false,
      render: (rowData) => <span>{rowData.date}</span>,
    },
  ];

  useEffect(() => {
    refetch();
  }, []);

  useEffect(() => {
    if (data) {
      setTotal(data.getSupplierSalesReport.paginatorInfo.lastPage);
    }
  }, [data, size]);

  const handlePageSizeChange = (newSize) => {
    setSize(newSize);
    setActivePage(1);
  };

  const handleReset = () => {
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    setBusiness(null);
    setTimeRange(null);
    setSetWarehouse(null);
    refetch();
  };

  const handleChange = (page) => {
    setActivePage(page);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("Sales Report", pageWidth / 2, 20, { align: "center" });

    let subtitle = "Date: ";
    if (selectedStartDate && selectedEndDate) {
      const startDate = selectedStartDate.toISOString().slice(0, 10);
      const endDate = selectedEndDate.toISOString().slice(0, 10);
      subtitle += `${startDate} - ${endDate}`;
    } else {
      subtitle += "All Date";
    }

    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.text(subtitle, pageWidth / 2, 28, { align: "center" });

    const bodyData = data?.getSupplierSalesReport.data.map((row) => [
      row.orderCode || "N/A",
      row.price || 0,
      row.numberOfItems || 0,
      row.supplier_business || "N/A",
      row.date || "N/A",
    ]);

    doc.autoTable({
      startY: 35,
      head: [headers],
      body: bodyData,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [27, 145, 223], textColor: [255, 255, 255] },
    });

    const finalY = doc.lastAutoTable.finalY || 10;
    doc.text(
      `Total Price: ${formatNumber(data.getSupplierSalesReport.total_price)}`,
      pageWidth - 20,
      finalY + 10,
      { align: "right" },
    );

    const exportedDate = `Exported Date: ${new Date().toLocaleString()}`;
    doc.setFontSize(10);
    doc.text(
      exportedDate,
      pageWidth - 14,
      doc.internal.pageSize.getHeight() - 10,
      { align: "right" },
    );

    doc.save("sales_report.pdf");
  };

  const exportToExcel = () => {
    const bodyData = data?.getSupplierSalesReport.data.map((row) => ({
      [headers[0]]: row.orderCode || "N/A",
      [headers[1]]: row.price || 0,
      [headers[2]]: row.numberOfItems || 0,
      [headers[3]]: row.supplier_business || "N/A",
      [headers[4]]: row.date || "N/A",
    }));

    bodyData.push({
      [headers[0]]: "Total Price",
      [headers[1]]: formatNumber(data.getSupplierSalesReport.total_price),
      [headers[2]]: "",
      [headers[3]]: "",
      [headers[4]]: "",
    });

    const worksheet = XLSX.utils.json_to_sheet(bodyData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sales");
    XLSX.writeFile(workbook, "sales_report.xlsx");
  };

  return (
    <div style={{ width: "98%", margin: "auto" }}>
      <LoadingOverlay
        visible={loading}
        color="blue"
        overlayBlur={2}
        loader={customLoader}
      />

      <div>
        <SimpleGrid cols={6}>
          <div>
            <Select
              data={[
                { value: "DAILY", label: "Daily" },
                { value: "WEEKLY", label: "Weekly" },
                { value: "MONTHLY", label: "Monthly" },
                { value: "QUARTERLY", label: "Half Year" },
                { value: "YEARLY", label: "Yearly" },
              ]}
              value={timeRange}
              onChange={(value) => setTimeRange(value)}
              label="Select Period"
              placeholder="Select Range"
              withinPortal
              clearable
            />
          </div>
          <div>
            <DatePicker
              value={selectedStartDate}
              onChange={setSelectedStartDate}
              placeholder="Pick a date"
              label="Select Start Date"
              clearable
            />
          </div>
          <div>
            <DatePicker
              value={selectedEndDate}
              onChange={setSelectedEndDate}
              placeholder="Pick a date"
              label="Select End Date"
              clearable
            />
          </div>
          <div>
            <BusinesFilter business={business} onCardClick={setBusiness} />
          </div>
          <div>
            <WarehouseFilter
              warehouse={warehouse}
              onCardClick={setSetWarehouse}
            />
          </div>
        </SimpleGrid>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "10px",
            gap: "10px",
          }}
        >
          {(timeRange ||
            selectedEndDate ||
            selectedStartDate ||
            warehouse ||
            business) && (
            <>
              <Button
                onClick={handleReset}
                style={{
                  width: "80px",
                  backgroundColor: "#FF6A00",
                  color: "#FFFFFF",
                }}
              >
                Cancel
              </Button>
            </>
          )}

          <Menu shadow="md" trigger="hover" openDelay={100} closeDelay={400}>
            <Menu.Target>
              <Button
                style={{
                  width: "80px",
                  backgroundColor: "#FF6A00",
                  color: "#FFFFFF",
                }}
              >
                Export
              </Button>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Item onClick={exportToPDF}>PDF</Menu.Item>
              <Menu.Item onClick={exportToExcel}>Excel</Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </div>
      </div>
      <Card shadow="sm" p="lg">
        <ScrollArea>
          <B2bTable
            total={total}
            activePage={activePage}
            handleChange={handleChange}
            header={headerData}
            data={data ? data.getSupplierSalesReport.data : []}
            size={size}
            searchInput={false}
            handlePageSizeChange={handlePageSizeChange}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              flexDirection: "column",
              p: 1,
              gap: 1,
              m: 1,
              bgcolor: "background.paper",
              borderRadius: 1,
            }}
          >
            <div
              style={{
                marginRight: "35px",
                fontWeight: "bold",
                fontSize: "15px",
              }}
            >
              Total Price:{" "}
              {formatNumber(data?.getSupplierSalesReport.total_price)}
            </div>
          </Box>
        </ScrollArea>
      </Card>
    </div>
  );
};

export default SalesReport;
